import { UseCreationStatusResult } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-creation-status";
import { T, useTranslation } from "@redotech/redo-i18n";
import FaceFrownIcon from "@redotech/redo-icons/face-frown.svg";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { Status } from "@redotech/redo-model/returns/temporal-status";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { TypeAnimation } from "react-type-animation";
import { Card } from "../../submission/components/card";
import * as confirmationCss from "../confirmation.module.css";

const EXPECTED_WORKFLOW_DURATION_MS = 30_000; // Increased since we're tracking the full workflow chain
const MAX_TIME_BASED_PROGRESS = 80; // Max progress from time alone (80%)
const ACTIVITY_BOOST = 1.5; // 1.5% per completed activity (reduced since we have fewer visible activities)
const ESTIMATED_TOTAL_ACTIVITIES = 25; // Reduced since we're only showing creation workflow activities
const ERROR_THRESHOLD_MS = 60_000; // Show error screen after 60 seconds

export const CreationStatus = memo(function CreationStatus({
  showCreationStatus,
  creationStatus,
  returnType = ReturnTypeEnum.RETURN,
}: {
  showCreationStatus: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  returnType?: ReturnTypeEnum;
}) {
  const { t } = useTranslation();
  if (!showCreationStatus) {
    return null;
  }

  const timeBetweenAnimations = 4000;

  // Check if workflow has exceeded error threshold
  const shouldShowError =
    creationStatus?.workflowStartTime &&
    Date.now() - new Date(creationStatus.workflowStartTime).getTime() >
      ERROR_THRESHOLD_MS;

  if (shouldShowError) {
    return (
      <Card fullscreen>
        <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
          <Icon arbiterIconSvg={FaceFrownIcon} color="ghost" size="large" />
          <Text
            color="text-primary"
            fontSize="xl"
            fontWeight="semibold"
            textAlign="center"
          >
            <T>Something is going wrong on our end</T>
          </Text>
          <Text
            color="text-secondary"
            fontSize="md"
            style={{ maxWidth: "500px" }}
            textAlign="center"
          >
            {returnType === ReturnTypeEnum.CLAIM
              ? t(
                  "Your shipping claim was still created. We will send you an email when we are back up so you can continue with your shipping claim process. This page will automatically update when we are back up. We are sorry for the inconvenience.",
                )
              : returnType === ReturnTypeEnum.WARRANTY
                ? t(
                    "Your warranty claim was still created. We will send you an email when we are back up so you can continue with your warranty claim process. This page will automatically update when we are back up. We are sorry for the inconvenience.",
                  )
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t(
                      "Your recycling request was still created. We will send you an email when we are back up so you can continue with your recycling request process. This page will automatically update when we are back up. We are sorry for the inconvenience.",
                    )
                  : t(
                      "Your return was still created. We will send you an email when we are back up so you can continue with your return process. This page will automatically update when we are back up. We are sorry for the inconvenience.",
                    )}
          </Text>
        </Flex>
      </Card>
    );
  }

  let percentage = 0;
  if (creationStatus?.activityStatuses) {
    const relevantActivities = creationStatus.activityStatuses || [];
    const completedActivities = relevantActivities.filter(
      (activity) => activity.status === Status.COMPLETED,
    ).length;
    const totalActivitiesStarted = relevantActivities.length;

    let timeProgress = 0;
    let elapsedMs = 0;
    if (creationStatus.workflowStartTime) {
      elapsedMs =
        Date.now() - new Date(creationStatus.workflowStartTime).getTime();
      timeProgress = Math.min(
        (elapsedMs / EXPECTED_WORKFLOW_DURATION_MS) * MAX_TIME_BASED_PROGRESS,
        MAX_TIME_BASED_PROGRESS,
      );
    }
    const estimatedTotal = Math.max(
      ESTIMATED_TOTAL_ACTIVITIES,
      totalActivitiesStarted + 3,
      completedActivities + 5,
    );

    const activityProgress = (completedActivities / estimatedTotal) * 100;
    const weightedProgress = timeProgress * 0.3 + activityProgress * 0.7;
    const activityBoost = completedActivities * ACTIVITY_BOOST;

    const baselineProgress = Math.min(elapsedMs > 0 ? 10 : 0, 10);

    const progress = Math.min(
      baselineProgress + weightedProgress + activityBoost,
      99,
    );

    if (creationStatus.isWorkflowRunning === false) {
      percentage = 100;
    } else {
      percentage = Math.round(Math.max(progress, percentage * 0.95));
    }
  }

  return (
    <Card fullscreen>
      <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
        <TypeAnimation
          className={confirmationCss.typeAnimationText}
          cursor={false}
          omitDeletionAnimation
          sequence={[
            returnType === ReturnTypeEnum.CLAIM
              ? t("Creating shipping claim...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Creating warranty claim...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Creating recycling request...")
                  : t("Creating return..."),
            timeBetweenAnimations,
            returnType === ReturnTypeEnum.CLAIM
              ? t("Generating shipping claim label...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Generating warranty claim label...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Generating recycling request label...")
                  : t("Generating return label..."),
            timeBetweenAnimations,
            t("Updating order..."),
            timeBetweenAnimations,
            t("Finishing up..."),
            timeBetweenAnimations,
            t("This is taking longer than expected..."),
          ]}
          speed={80}
          style={{ height: "20px" }}
          wrapper="span"
        />

        <Flex dir="column" gap="sm" style={{ maxWidth: "400px" }} w="full">
          <RedoProgressBar percentage={percentage} />
          <Text color="text-secondary" fontSize="xs" textAlign="center">
            <T>{{ percentage: Math.round(percentage) }}% complete</T>
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
});
